import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, ArrowRight, Send, CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import './Contact.css';
import { API_URL } from '../config';

const ContactInput = ({ label, type = "text", placeholder, isTextArea = false, value, onChange, required = false }) => {
  const [isFocused, setIsFocused] = useState(false);

  const Tag = isTextArea ? "textarea" : "input";

  return (
    <div className="contact-field">
      <motion.label
        className="contact-field-label"
        initial={false}
        animate={{
          y: -22,
          scale: 0.8,
          color: 'var(--primary)'
        }}
        transition={{ duration: 0.25 }}
      >
        {label}
      </motion.label>
      <Tag
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`contact-field-input ${isTextArea ? 'contact-textarea' : ''}`}
        placeholder={isFocused ? placeholder : ''}
      />
      <div className="contact-field-glow" style={{ width: isFocused ? '100%' : '0%', opacity: isFocused ? 1 : 0 }}></div>
    </div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e, field) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        Swal.fire({
          icon: 'success',
          title: 'Mesajınız Alındı!',
          text: 'Bizlere ulaştığınız için teşekkür ederiz. En kısa sürede size geri dönüş sağlayacağız.',
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#00c2ff',
          customClass: {
            popup: 'premium-swal-popup'
          }
        });
        setFormData({ name: '', surname: '', phone: '', message: '' });
        
        // Reset submitted state after some time to allow new messages
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Gönderim hatası');
      }
    } catch (err) {
      console.error('İletişim Hatası:', err);
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Mesaj gönderilirken bir sorun oluştu. Lütfen bilgilerinizi kontrol edip tekrar deneyin.',
        background: '#0f172a',
        color: '#fff',
        confirmButtonColor: '#00c2ff'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-main)', minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Navbar />

      {/* Ambient Lights */}
      <div className="ambient-light" style={{ top: '-10%', right: '-10%', opacity: 0.15 }}></div>
      <div className="ambient-light ambient-light-silver" style={{ bottom: '-15%', left: '-10%', opacity: 0.08 }}></div>

      <main style={{ paddingTop: '160px', paddingBottom: '80px' }}>
        <div className="container" style={{ position: 'relative', zIndex: 10 }}>

          {/* Page Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.23, 1, 0.32, 1] }}
            style={{ marginBottom: '60px', textAlign: 'center' }}
          >
            <h1 className="section-title">İletişime<span>Geçin.</span></h1>
            <p className="text-text-muted" style={{ fontSize: '1.1rem', marginTop: '16px', fontWeight: 300 }}>
              Araç kiralama, sorularınız veya iş birlikleriniz için bize ulaşın.
            </p>
          </motion.div>

          <div className="contact-grid">
            
            {/* LEFT — Form Card */}
            <motion.div
              className="contact-card-container"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="contact-form-header">
                  <h3 className="contact-form-title">Mesaj Gönderin</h3>
                  <p className="contact-form-desc">Formu doldurarak bizimle iletişime geçebilirsiniz. En kısa sürede size dönüş yapacağız.</p>
                </div>

                <div className="contact-form-row">
                  <ContactInput 
                    label="Ad *" 
                    placeholder="Adınız" 
                    value={formData.name}
                    onChange={(e) => handleChange(e, 'name')}
                    required
                  />
                  <ContactInput 
                    label="Soyad *" 
                    placeholder="Soyadınız" 
                    value={formData.surname}
                    onChange={(e) => handleChange(e, 'surname')}
                    required
                  />
                </div>

                <div className="contact-form-row">
                  <ContactInput 
                    label="Telefon Numarası *" 
                    type="tel" 
                    placeholder="05xx xxx xx xx" 
                    value={formData.phone}
                    onChange={(e) => handleChange(e, 'phone')}
                    required
                  />
                </div>

                <ContactInput 
                  label="Mesajınız *" 
                  isTextArea 
                  placeholder="Lütfen mesajınızı detaylı bir şekilde yazın..." 
                  value={formData.message}
                  onChange={(e) => handleChange(e, 'message')}
                  required
                />

                <div className="contact-checkbox-wrapper">
                  <input type="checkbox" id="aydinlatma" className="contact-checkbox" required />
                  <label htmlFor="aydinlatma" className="contact-checkbox-label">
                    <Link to="/kvkk" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>İletişim Formu Aydınlatma Metni</Link>'ni okudum ve kabul ediyorum. *
                  </label>
                </div>

                <motion.button
                  type="submit"
                  className={`contact-submit-btn ${submitted ? 'success' : ''}`}
                  whileHover={{ scale: submitted ? 1 : 1.015 }}
                  whileTap={{ scale: submitted ? 1 : 0.985 }}
                  disabled={loading || submitted}
                >
                  {submitted ? <CheckCircle size={18} /> : <Send size={18} />}
                  <span>{loading ? 'Gönderiliyor...' : (submitted ? 'Mesaj Alındı' : 'Mesajı Gönder')}</span>
                </motion.button>
              </form>
            </motion.div>

            {/* RIGHT — Map Card */}
            <motion.div
              className="contact-card-container"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <div className="contact-map-card">
                <div className="contact-map-header">
                  <h3 className="contact-map-title">
                    <MapPin size={24} color="#ef4444" fill="rgba(239, 68, 68, 0.2)" />
                    Konumumuz
                  </h3>
                  <p className="contact-map-desc">Ofisimize nasıl ulaşabileceğinizi haritadan görebilirsiniz.</p>
                </div>
                
                <div className="contact-map-embed">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3161.700105568045!2d36.84751371167564!3d37.58567892291315!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x152ddbcbc1b1a123%3A0xe7c87d2054cb16ca!2sErenis%20Otomotiv%20Ara%C3%A7%20Kiralama!5e0!3m2!1str!2str!4v1776521466308!5m2!1str!2str" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Erenis Otomotiv Konum"
                  ></iframe>
                </div>

                <div className="contact-info-footer">
                  <div className="contact-info-item">
                    <Phone size={16} />
                    <span>+90 532 158 56 46</span>
                  </div>
                  <div className="contact-info-item">
                    <Mail size={16} />
                    <span>info@erenisotomotiv.com</span>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
